<script setup lang="ts">

import type {PortfolioFiltersFormType, PortfolioType} from "~/types/Portfolio";
import {endpoints} from "~/constants/endpoints";
import noResults from '~/assets/img/no_results.svg'
import { Tabs, TabsList, TabsTrigger } from '~/components/ui/tabs'
import { ScrollArea } from '~/components/ui/scroll-area'
import { Dialog, DialogTrigger, DialogContent, DialogClose } from '~/components/ui/dialog'
import { Button } from '~/components/ui/button'
import GeoCatalogFilters from '~/components/pages/catalog/GeoCatalogFilters.vue'
import GeoMasterCard from '~/components/pages/catalog/GeoMasterCard.vue'
import GoogleMap from "~/components/custom-ui/GoogleMap.vue";
import UserAvatar from '~/components/custom-ui/UserAvatar.vue'
import {render} from "vue";
import type { MapLocation } from "~/composables/useGoogleMap";
import { useMediaQuery } from '@vueuse/core'

const route = useRoute();
const router = useRouter()
const globalStore = useGlobalStore()
const config = useRuntimeConfig()
const isMobile = useMediaQuery('(max-width: 768px)')

const AsideEnum = {
    MASTERS: 'masters',
    FILTERS: 'filters',
} as const;

const activeAside = ref<typeof AsideEnum[keyof typeof AsideEnum]>(AsideEnum.MASTERS);
const filtersModalIsOpen = ref(false)

const specificationId = computed(() => {
    if(globalStore.specifications.find(item => item.documentId === route.params.id)){
        return route.params.id
    }
    const foundSpecification = globalStore.specifications.find(item => item.slug === route.params.slug)?.documentId
    if (!foundSpecification) {
        throw createError({
            statusCode: 404,
            message: 'Specification not found'
        })
    }

    return foundSpecification
})

const {
    isLoaded,
    mastersList,
    filterForm,
    total,
    filtersList,
    currentSpecification,
    parentSpecification,
    specificationChildList,
    cleanedFilterForm,
    getFilters,
} = usePortfolio(specificationId.value as string);

const mapRef = ref<InstanceType<typeof GoogleMap> | null>(null)
const isMapReady = ref(false)
const center = ref<MapLocation | null>(null)
const {
    getCurrentLocation,
    isLoadingLocation,
    goToCurrentLocation: goToCurrentLocationBase,
} = useGoogleMap()

const bounds = ref<{ minLat: number; maxLat: number; minLng: number; maxLng: number; } | null>(null)
const pendingBounds = ref<{ minLat: number; maxLat: number; minLng: number; maxLng: number } | null>(null)

const goToCurrentLocation = () => {
    goToCurrentLocationBase((location: MapLocation, address: string | null) => {
        center.value = location
        if (mapRef.value) {
            mapRef.value.setLocation(location)
        }
    })
}

await getFilters()
/* await getMasters() */

async function getMasters() {
    if (!specificationId.value) return []
    await nextTick()
    let query = {
        ...cleanedFilterForm.value,
        pageSize: 15,
    }

    if(bounds.value){
        query = {
            ...query,
            ...(bounds.value as Record<string, any>)
        }
    }
    
    const response = await fetchData<PortfolioType[]>(endpoints.portfolio.search, {
        method: 'GET',
        query: query
    }).then(({data, meta}) => {
        return data
    }).catch(e => {
        console.error(e)
    })
    mastersList.value = response?.filter(portfolio => !!portfolio.geo && !!portfolio.user) || []
}

async function submitFilters(localFilters: any){
    filterForm.value = localFilters
    await getMasters();
    activeAside.value = AsideEnum.MASTERS
    filtersModalIsOpen.value = false
}

onMounted(async () => {
    try {
        const location = await getCurrentLocation()
        center.value = location
        bounds.value = useBoundsCalculator(location).value
        if (mapRef.value) {
            mapRef.value.setLocation(location)
        }
    } catch (error: any) {
        const defaultLocation: MapLocation = { lat: 25.057257, lng: 55.207695 }
        center.value = defaultLocation
        bounds.value = useBoundsCalculator(defaultLocation).value
        if (error?.code !== 3) {
            console.warn('Error getting location, using default coordinates:', error?.message || error)
        }
    }
})

const applyFitBounds = (boundsToApply: { minLat: number; maxLat: number; minLng: number; maxLng: number }) => {
    if (!mapRef.value || !isMapReady.value) {
        pendingBounds.value = boundsToApply
        return
    }
    
    const map = mapRef.value.getMap()
    if (!map) {
        pendingBounds.value = boundsToApply
        return
    }
    
    nextTick(() => {
        mapRef.value?.fitBounds(boundsToApply, 50)
        pendingBounds.value = null
    })
}

const mastersMarkers = computed(() => {
    return mastersList.value
        .filter(item => item.geo && item.user)
        .map(item => {
            const markerContainer = document.createElement('div')
            markerContainer.className = 'relative w-11 h-11 rounded-full border-2 border-white shadow-md cursor-pointer flex items-center justify-center'
            
            const avatarUrl = item.user?.avatar?.url || (item.user?.avatar as any)?.formats?.small?.url
            
            render(
                h(UserAvatar, {
                    src: avatarUrl,
                    name: item.user.username,
                    size: 'xs',
                    onClick: () => {
                        navigateTo({
                            name: 'portfolio',
                            params: {id: item.documentId},
                            query: {mode: 'geo'}
                        })
                    }
                }),
                markerContainer
            )

            return {
                documentId: item.documentId,
                name: item.user.username,
                position: {
                    lat: item.geo!.lat,
                    lng: item.geo!.lng,
                },
                content: markerContainer
            }
        })
})

const handleMapReady = () => {
    isMapReady.value = true
    if (pendingBounds.value) {
        setTimeout(() => {
            applyFitBounds(pendingBounds.value!)
        }, 100)
    }
}

watch(bounds, async (newBounds) => {
    if (newBounds) {
        await getMasters()
        applyFitBounds(newBounds)
    }
}, { immediate: true, deep: true })

useSeoMeta({
    robots: 'index, follow',
    title: currentSpecification.value?.name,
    description: () => currentSpecification.value?.description,
    ogImage: currentSpecification.value?.thumbnail?.url,
    ogImageType: 'image/jpeg',
    ogType: 'website',
    ogUrl: () => config.public.frontUrl + route.path,
    ogTitle: () => currentSpecification.value?.name,
    ogDescription: () => currentSpecification.value?.description,
    twitterTitle: () => currentSpecification.value?.name,
    twitterDescription: () => currentSpecification.value?.description,
    twitterImage: currentSpecification.value?.thumbnail?.url,
    twitterImageType: 'image/jpeg',
})

</script>

<template>
    <div class="">
    <div class="mobile_header md:hidden grid grid-cols-6 items-center mb-4">
        <div class="col-span-1">
            <Button @click.prevent="router.back()" variant="ghost" class="p-0">
                <Icon name="ri:arrow-left-line" size="24px" />
            </Button>
        </div>
        <div class="col-span-4">
            <p class="text-center font-bold uppercase text-[13px] tracking-widest leading-none">{{ $t('Specialists nearby') }}</p>
        </div>
        <div class="col-span-1">
        </div>
    </div>
    <div class="grid gap-4 lg:gap-12 lg:grid-cols-12">
        <div v-if="activeAside === AsideEnum.MASTERS" class="aside order-2 lg:order-1 lg:col-span-4">
            <div class="hidden lg:block page_title mb-8">
                <div class="flex items-center gap-6">
                    <Button @click.prevent="router.back()" class="p-3 h-auto text-black" variant="lighter">
                        <Icon name="ri:arrow-left-line" size="24px"/>
                    </Button>
                    <p class="font-semibold text-xl">{{ currentSpecification?.name }}</p>
                </div>
            </div>
            
            <div class="mb-4">
                <Button class="hidden lg:flex p-3 h-auto w-full rounded-lg text-dark" variant="lighter"
                          @click.prevent="activeAside = AsideEnum.FILTERS">
                    <Icon name="ri:equalizer-line" size="24px"/>
                    <span class="text-sm font-medium ml-2">{{ $t('Filters') }}</span>  
                </Button>
                
                <Dialog v-model:open="filtersModalIsOpen">
                    <DialogTrigger as-child>
                        <Button class="flex lg:hidden p-3 h-auto w-full rounded-lg text-dark" variant="lighter">
                            <Icon name="ri:equalizer-line" size="24px"/>
                            <span class="text-sm font-medium ml-2">{{ $t('Filters') }}</span>
                        </Button>
                    </DialogTrigger>
                    <DialogContent class="!p-4 gap-0 ys-modal-dialog">
                        <DialogClose class="absolute top-4 ltr:right-4 rtl:left-4">
                            <Icon name="ri:close-line" size="20px"
                                  class="text-gray-500 translate-all hover:text-gray-700" />
                        </DialogClose>
                        <GeoCatalogFilters
                                :filters-list="filtersList"
                                :masters-count="mastersList.length"
                                :parent-specification="parentSpecification"
                                :specificationChildList="specificationChildList"
                                :filterForm="filterForm"
                                @submit="submitFilters"
                                @back="activeAside = AsideEnum.MASTERS"
                                @closeFiltersModal="filtersModalIsOpen = false"
                        />
                    </DialogContent>
                </Dialog>
                
                <div class="masters_count text-center text-gray-400 text-sm mt-2">
                    {{ mastersList.length }} {{ $t('specialists nearby') }}
                </div>
            </div>

            <template v-if="isLoaded">
                <ScrollArea v-if="mastersList.length" class="h-[600px]">
                    <div class="grid gap-7">
                        <GeoMasterCard
                                v-for="item in mastersList"
                                :current_specification="currentSpecification?.documentId"
                                :item="item"
                                :parent_specification="parentSpecification?.documentId"
                        />
                    </div>
                </ScrollArea>

                <div v-else class="">
                    <div class="p-4 rounded-md font-bold text-gray-500 text-xs text-center bg-gray-100">
                        <p>{{$t('Unfortunately, there are no masters on the map according to the selected filters. Try to change filtering parameters and repeat the search.')}}</p>
                    </div>
                    <img src="" alt="">
                    <noResults class="mt-8 max-w-[270px] mx-auto" />
                </div>
            </template>
        </div>
        
        <div v-if="activeAside === AsideEnum.FILTERS" class="aside order-2 lg:order-1 lg:col-span-4">
            <GeoCatalogFilters
                :filters-list="filtersList"
                :masters-count="mastersList.length"
                :parent-specification="parentSpecification"
                :specificationChildList="specificationChildList"
                :filterForm="filterForm"
                @submit="submitFilters"
                @back="activeAside = AsideEnum.MASTERS"
                @closeFiltersModal="filtersModalIsOpen = false"
            />
        </div>

        <div class="order-1 lg:order-2 col-span-1 lg:col-span-8">
            <GoogleMap 
                ref="mapRef"
                :center="center || {lat: 25.057257, lng: 55.207695}"
                :show-marker="true"
                :clickable="false"
                aspect-ratio="1/1"
                min-height="100%"
                @ready="handleMapReady"
            >
                <template #markers>
                    <ScriptGoogleMapsMarkerClusterer>
                        <ScriptGoogleMapsAdvancedMarkerElement
                                v-for="marker in mastersMarkers"
                                :key="marker.documentId"
                                :options="{ 
                                    position: marker.position,
                                    content: marker.content
                                }"
                            />
                    </ScriptGoogleMapsMarkerClusterer>
                </template>
                <div class="absolute top-2 left-2 z-10">
                    <Tabs
                        @update:modelValue="(mode: string) => router.push({name: route.name, params: {slug: route.params.slug as string, city: route.params.city as string}, query: {mode: 'list'}})"
                        default-value="geo"
                        class="bg-white rounded-xl"
                    >
                        <TabsList>
                            <TabsTrigger value="geo" class="rounded-xl py-3 data-[state=active]:bg-primary-500 data-[state=active]:text-white">
                                <Icon name="ri:map-2-line" size="24" />
                            </TabsTrigger>
                            <TabsTrigger value="list" class="rounded-xl py-3 data-[state=active]:bg-primary-500 data-[state=active]:text-white" >
                                <Icon name="ri:list-check-2" size="24" />
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>
                <div class="absolute top-1/2 -translate-y-1/2 right-4 flex flex-col gap-1">
                    <div class="flex flex-col bg-white shadow-lg w-10 z-10 rounded">
                        <Button
                            variant="ghost"
                            size="icon"
                            @click="mapRef?.zoomIn()"
                            :disabled="isLoadingLocation"
                        >
                            <Icon 
                                name="ri:add-fill" 
                                size="20"
                            />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            @click="mapRef?.zoomOut()"
                            :disabled="isLoadingLocation"
                        >
                            <Icon 
                                name="ri:subtract-fill" 
                                size="20"
                            />
                        </Button>
                    </div>
                    
                    
                    <Button
                        variant="outline"
                        size="icon"
                        class="bg-white shadow-lg h-10 w-10 z-10"
                        @click="goToCurrentLocation"
                        :disabled="isLoadingLocation"
                    >
                        <Icon 
                            v-if="isLoadingLocation"
                            name="svg-spinners:ring-resize" 
                            size="20" 
                        />
                        <Icon 
                            v-else
                            name="ri:send-plane-fill" 
                            size="20" 
                            class="text-primary-500"
                        />
                    </Button>
                </div>
            </GoogleMap>
        </div>
    </div>
    </div>
</template>